import React, { useState, useEffect } from "react";
import TopBar from "../../common/TopBar";
import Sidebar from "../../common/Sidebar";
import { DataGrid } from "@mui/x-data-grid";
import { ToastContainer, toast } from "react-toastify";
import {
    Button,
    Box,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
} from "@mui/material";
import { FiEdit, FiTrash2, FiPlus } from "react-icons/fi";

// API SERVICES
import {
    fetchAllTourPlaces,
    createTourPlace,
    updateTourPlace,
    deleteTourPlace,
} from "../../../services/adminRestapiService";



function TourPlaces() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [tourPlaces, setTourPlaces] = useState([]);
    const [loading, setLoading] = useState(true);

    // Add Modal
    const [openAddModal, setOpenAddModal] = useState(false);
    const [newPlace, setNewPlace] = useState({
        category: "",
        display_title: "",
    });

    // Edit modal
    const [openEditModal, setOpenEditModal] = useState(false);
    const [editPlace, setEditPlace] = useState({
        id: "",
        category: "",
        display_title: "",
    });

    // SIDEBAR TOGGLE
    const toggleSidebar = () => setSidebarOpen((isCurrentlyOpen) => !isCurrentlyOpen);

    // FETCH DATA
    useEffect(() => {
        loadPlaces();
    }, []);

    const loadPlaces = async () => {
        try {
            const res = await fetchAllTourPlaces();
            const data = res.data.data.map((item) => ({
                ...item,
                id: item.id,
            }));
            setTourPlaces(data);
        } catch (error) {
            toast.error("Failed to fetch display titles");
        } finally {
            setLoading(false);
        }
    };

    // ADD INPUT HANDLER
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewPlace({ ...newPlace, [name]: value });
    };

    // EDIT INPUT HANDLER
    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setEditPlace({ ...editPlace, [name]: value });
    };

    // ADD SUBMIT
    const handleAddSubmit = async () => {
        try {
            const requestBody = {
                category: newPlace.category,
                display_title: newPlace.display_title,
            };

            await createTourPlace(requestBody);

            toast.success("Display title added successfully");
            setOpenAddModal(false);
            setNewPlace({ category: "", display_title: "" });
            loadPlaces();
        } catch (err) {
            toast.error("Failed to add");
        }
    };

    // EDIT CLICK
    const handleEditClick = (row) => {
        setEditPlace({ ...row });
        setOpenEditModal(true);
    };

    // EDIT SUBMIT
    const handleEditSubmit = async () => {
        try {
            const token = localStorage.getItem("token");

            await updateTourPlace(token, editPlace.id, {
                category: editPlace.category,
                display_title: editPlace.display_title
            });


            toast.success("Updated successfully");
            setOpenEditModal(false);
            loadPlaces();
        } catch (err) {
            toast.error("Failed to update");
        }
    };

    // DELETE
    const handleDeleteClick = async (id) => {
        if (!window.confirm("Are you sure you want to delete?")) return;

        try {
            await deleteTourPlace(id);
            toast.success("Deleted!");
            loadPlaces();
        } catch (err) {
            toast.error("Failed to delete");
        }
    };

    // DATAGRID COLUMNS
    const columns = [
        { field: "id", headerName: "ID", flex: 0.5 },
        { field: "category", headerName: "Category", flex: 1 },
        { field: "display_title", headerName: "Display Title", flex: 2 },
        { field: "created_at", headerName: "Created At", flex: 1.5 },
        {
            field: "actions",
            headerName: "Actions",
            flex: 1,
            renderCell: (params) => (
                <Box className="flex gap-2">
                    <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleEditClick(params.row)}
                    >
                        <FiEdit />
                    </IconButton>
                    <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDeleteClick(params.row.id)}
                    >
                        <FiTrash2 />
                    </IconButton>
                </Box>
            ),
        },
    ];

    return (
        <div className="flex min-h-screen bg-slate-50">
            <ToastContainer />

            <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

            <div
                className={`min-h-screen min-w-0 flex-1 transition-[margin] duration-300 ${sidebarOpen ? "lg:ml-64" : "lg:ml-20"
                    }`}
            >
                <TopBar toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />

                <main className="px-4 pb-8 pt-24 sm:px-6 lg:px-8">
                    <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <h2 className="text-xl font-semibold text-slate-900 sm:text-2xl">Home Page Main Titles</h2>
                        <Button
                            variant="contained"
                            onClick={() => setOpenAddModal(true)}
                            sx={{ borderRadius: "50%", minWidth: 40, minHeight: 40 }}
                        >
                            <FiPlus size={20} />
                        </Button>
                    </div>

                    {/* DATAGRID */}
                    <DataGrid
                        rows={tourPlaces}
                        columns={columns}
                        autoHeight
                        loading={loading}
                        pageSize={10}
                    />

                    {/* ADD MODAL */}
                    <Dialog open={openAddModal} onClose={() => setOpenAddModal(false)}>
                        <DialogTitle>Add Display Title</DialogTitle>
                        <DialogContent className="space-y-4 mt-2">
                            <input
                                type="text"
                                name="category"
                                placeholder="Category"
                                value={newPlace.category}
                                onChange={handleInputChange}
                                className="w-full p-2 border rounded"
                            />

                            <input
                                type="text"
                                name="display_title"
                                placeholder="Display Title"
                                value={newPlace.display_title}
                                onChange={handleInputChange}
                                className="w-full p-2 border rounded"
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setOpenAddModal(false)}>Cancel</Button>
                            <Button variant="contained" onClick={handleAddSubmit}>
                                Add
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* EDIT MODAL */}
                    <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)}>
                        <DialogTitle>Edit Display Title</DialogTitle>
                        <DialogContent className="space-y-4 mt-2">
                            <input
                                type="text"
                                name="category"
                                placeholder="Category"
                                value={editPlace.category}
                                onChange={handleEditInputChange}
                                className="w-full p-2 border rounded"
                            />

                            <input
                                type="text"
                                name="display_title"
                                placeholder="Display Title"
                                value={editPlace.display_title}
                                onChange={handleEditInputChange}
                                className="w-full p-2 border rounded"
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setOpenEditModal(false)}>Cancel</Button>
                            <Button variant="contained" onClick={handleEditSubmit}>
                                Update
                            </Button>
                        </DialogActions>
                    </Dialog>
                </main>
            </div>
        </div>
    );
}

export default TourPlaces;
